'use client';

/**
 * Sort dropdown for /recipes — URL-param driven.
 *
 * Writes `?sort=<key>` for the field and `?dir=asc|desc` for the override.
 * Selecting "Default" clears `sort` (and `dir`) so the page falls back to
 * its default ordering (Trending in browse mode, Relevance in search mode).
 *
 * The direction toggle (↑/↓) folds into the sort menu itself (the "Order"
 * group). It's hidden for `relevance` (no inverse) and when no sort is
 * selected. Switching to a different sort resets `dir` so the new field
 * starts in its natural direction.
 */

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useTransition } from 'react';
import { useTranslations } from 'next-intl';
import { SortControl, type SortOption } from '@/components/ui/sort-control';
import {
  parseSortKey,
  parseSortDir,
  naturalSortDir,
  effectiveSortDir,
  sortSupportsDirection,
  type SortKey,
} from '@/lib/utils/parse-recipe-filters';
import styles from './sort-dropdown.module.css';

interface SortDropdownProps {
  /** Whether a query is active. Drives the `relevance` option's visibility. */
  hasQuery: boolean;
}

export function SortDropdown({ hasQuery }: SortDropdownProps) {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const current = parseSortKey(searchParams.get('sort') ?? undefined);
  const dir = parseSortDir(searchParams.get('dir') ?? undefined);

  const pushParams = (next: URLSearchParams) => {
    const qs = next.toString();
    startTransition(() => {
      router.push(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    });
  };

  const handleSortChange = (next: string) => {
    const params = new URLSearchParams(searchParams.toString());
    if (next) params.set('sort', next);
    else params.delete('sort');
    // Switching sort fields resets dir to that sort's natural direction —
    // a sticky `dir` would mean a calorie sort suddenly flipping to DESC
    // because the user previously set "Newest → ASC".
    params.delete('dir');
    pushParams(params);
  };

  const handleDirToggle = () => {
    if (!current || !sortSupportsDirection(current)) return;
    const nextDir = effectiveSortDir(current, dir) === 'asc' ? 'desc' : 'asc';
    const params = new URLSearchParams(searchParams.toString());
    // Only persist `dir` when it differs from natural so the URL stays clean.
    if (nextDir === naturalSortDir(current)) {
      params.delete('dir');
    } else {
      params.set('dir', nextDir);
    }
    pushParams(params);
  };

  // Options excluding `relevance` when there's no query. Keeping the value
  // live in the URL so a shared link ?sort=relevance still parses — the
  // discovery layer falls back to trending. `key: ''` clears the sort back
  // to the page default.
  const options: SortOption[] = [
    { key: '', label: t('recipes.sort.default'), icon: 'sort' },
    ...(hasQuery
      ? [{ key: 'relevance' as SortKey, label: t('recipes.sort.relevance'), icon: 'sparkles' as const }]
      : []),
    { key: 'newest', label: t('recipes.sort.newest'), icon: 'arrow-down' },
    { key: 'trending', label: t('recipes.sort.trending'), icon: 'flame' },
    { key: 'fastest', label: t('recipes.sort.fastest'), icon: 'timer' },
    { key: 'calories', label: t('recipes.sort.calories'), icon: 'scale' },
  ];

  const showDirToggle = current !== null && sortSupportsDirection(current);
  const currentDir = current ? effectiveSortDir(current, dir) : 'desc';

  return (
    <div className={styles.wrap} data-pending={isPending ? 'true' : undefined}>
      <SortControl
        value={current ?? ''}
        onChange={handleSortChange}
        options={options}
        ariaLabel={t('recipes.sort.label')}
        dir={showDirToggle ? currentDir : undefined}
        onToggleDir={showDirToggle ? handleDirToggle : undefined}
        orderLabel={t('recipes.sort.order')}
        dirAscLabel={t('recipes.sort.orderAsc')}
        dirDescLabel={t('recipes.sort.orderDesc')}
      />
    </div>
  );
}
